// approverExportSlice.ts

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import { generateFileName } from '../../../utils/date'

export type ApproverExportType = 'history'

export interface ApproverExportPayload {
  date: string
  startDate?: string
  endDate?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  timeZone?: string

  filters?: {
    dateOption?: string
    department?: string[]
    union?: string[]
    approvalGroup?: string[]
    jobCode?: string[]
    status?: string[]
    requestType?: string[]
  }
}

interface ExportState {
  loading: boolean
  error: string | null
  success: boolean
}

const initialState: ExportState = {
  loading: false,
  error: null,
  success: false,
}

export const exportApproverRequests = createAsyncThunk<
  boolean,
  {
    type?: ApproverExportType
    payload: ApproverExportPayload
  },
  {
    rejectValue: string
  }
>(
  'approverExport/exportApproverRequests',
  async ({ type, payload }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        endpoints.approver.export(type),
        payload,
        {
          responseType: 'blob',
        }
      )

      if (!(response.data instanceof Blob) || response.data.size === 0) {
        return rejectWithValue('Export returned an empty file')
      }

      const filename = generateFileName()

      const url = URL.createObjectURL(response.data)

      try {
        const link = document.createElement('a')

        link.href = url
        link.download = filename

        document.body.appendChild(link)
        link.click()
        link.remove()

        return true
      } finally {
        URL.revokeObjectURL(url)
      }
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to export approver requests'
        )
      )
    }
  }
)

const approverExportSlice = createSlice({
  name: 'approverExport',
  initialState,

  reducers: {
    clearExportState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(exportApproverRequests.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(exportApproverRequests.fulfilled, (state) => {
        state.loading = false
        state.success = true
      })
      .addCase(exportApproverRequests.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = action.payload ?? 'Export failed'
      })
  },
})

export const { clearExportState } = approverExportSlice.actions

export default approverExportSlice.reducer